import { useState, useEffect, useCallback } from 'react';

/* ============================================================================
   INSTALL TO HOME SCREEN (PWA)

   Eduvora installs from the browser — no app store, no separate mobile build.

   The awkward truth this component exists to handle: the install flow is NOT
   the same everywhere, and we don't fully control it.

     Android / Chrome / Edge — the browser fires `beforeinstallprompt`. We catch
       it, stash it, and show our own button that triggers the real prompt.

     iOS Safari — NEVER fires that event. Apple only allows install via
       Share → Add to Home Screen. So on iPhone we show *instructions*, not a
       button that would do nothing. A dead button is worse than no button.

     Already installed — we show nothing at all. Nagging someone to install an
       app they're already using is the kind of detail that makes software feel
       cheap.
   ========================================================================== */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'eduvora.install.dismissed';

/** Running as an installed app rather than in a browser tab? */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS reports it here instead
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();                   // stop Chrome's own mini-infobar
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);                      // the event can only be used once
    return outcome === 'accepted';
  }, [deferred]);

  return {
    installed,
    /** Android/desktop: a real prompt is available. */
    canPrompt: !!deferred,
    /** iOS: no prompt exists; we must show instructions instead. */
    needsIosHelp: isIos() && !installed,
    install,
  };
}

/* -------------------------------------------------------------------------- */

/** A small "Install app" button for the top bar. Renders nothing when it can't help. */
export function InstallButton() {
  const { installed, canPrompt, install } = useInstallPrompt();//needsIosHelp
  const [showIos, setShowIos] = useState(false);

  // Already installed → nothing to offer.
  if (installed) return null;

  /**
   * ⚠ THIS USED TO SAY:  if (!canPrompt && !needsIosHelp) return null;
   *
   * That is why you never saw the button.
   *
   * `canPrompt` only turns true when Chrome fires `beforeinstallprompt` — and Chrome DELAYS that
   * event. Sometimes 30+ seconds. Sometimes until a second visit. It never fires on desktop at all.
   *
   * So the button RENDERED NOTHING while you were looking for it, and you reasonably concluded the
   * PWA was broken.
   *
   * Now the button is ALWAYS shown when the app isn't installed. If Chrome hasn't handed us the
   * prompt yet, we show instructions instead of vanishing. A visible button that explains itself
   * beats an invisible one that works perfectly.
   */
  return (
    <>
      <button
        className="btn install-btn"
        onClick={() => (canPrompt ? void install() : setShowIos(true))}
        title="Install Eduvora on this device"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
        </svg>
        <span className="install-btn-text">Install app</span>
      </button>

      {showIos && <IosInstructions onClose={() => setShowIos(false)} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * A dismissible banner for the family portal — parents are the ones who
 * genuinely benefit from a home-screen icon. Shown once; dismissing it sticks.
 */
export function InstallBanner() {
  const { installed, canPrompt, needsIosHelp, install } = useInstallPrompt();
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (installed || dismissed) return null;
  if (!canPrompt && !needsIosHelp) return null;

  return (
    <>
      <div className="install-banner" role="region" aria-label="Install Eduvora">
        <div className="install-banner-icon" aria-hidden="true">
          <img src="/app-icon-192.png" alt="" width="40" height="40" />
        </div>
        <div className="install-banner-text">
          <strong>Add Eduvora to your home screen</strong>
          <span>Open it like an app — attendance, fees and results, one tap away.</span>
        </div>
        <div className="install-banner-actions">
          <button
            className="btn btn-accent"
            onClick={() => (canPrompt ? void install() : setShowIos(true))}
          >
            Install
          </button>
          <button className="btn install-dismiss" onClick={dismiss} aria-label="Not now">
            Not now
          </button>
        </div>
      </div>

      {showIos && <IosInstructions onClose={() => setShowIos(false)} />}
    </>
  );
}

/* -------------------------------------------------------------------------- */

/** iPhone has no install API — so we show the person exactly where to tap. */
/**
 * The instructions.
 *
 * ⚠ Now reachable from ANY platform — because the button no longer hides itself when Chrome hasn't
 * fired `beforeinstallprompt` yet. So it must not show an iPhone user's steps to an Android user.
 */
function IosInstructions({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);

  // Not a phone at all — a desktop browser. Chrome CAN install, but only via its own menu.
  if (!isIos && !isAndroid) {
    return (
      <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
        <div className="sheet" onClick={(e) => e.stopPropagation()}>
          <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>Install Eduvora</h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 16 }}>
            In Chrome or Edge, look for the <strong>install icon</strong> in the address bar
            (a small screen with a down-arrow), or open the <strong>⋮ menu → Install Eduvora</strong>.
          </p>
          <p style={{ color: 'var(--ink-3)', fontSize: 12.5, marginBottom: 18 }}>
            It's most useful on a phone — try it on your mobile.
          </p>
          <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
            onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    );
  }

  // Android, but Chrome hasn't offered us the prompt yet. Tell them the truth.
  if (isAndroid) {
    return (
      <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
        <div className="sheet" onClick={(e) => e.stopPropagation()}>
          <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>
            Add to your home screen
          </h2>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 16 }}>
            Open the <strong>⋮ menu</strong> in Chrome (top right), then tap{' '}
            <strong>Install app</strong> — or <strong>Add to Home screen</strong>.
          </p>
          <p style={{ color: 'var(--ink-3)', fontSize: 12.5, marginBottom: 18 }}>
            Chrome sometimes takes a few seconds before it offers to install automatically. The menu
            always works.
          </p>
          <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
            onClick={onClose}>
            Got it
          </button>
        </div>
      </div>
    );
  }

  // iOS. Apple gives no install API at all — Share → Add to Home Screen is the only way.
  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true"
         aria-label="How to install on iPhone">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>
          Add to your home screen
        </h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 14, marginBottom: 18 }}>
          On iPhone and iPad, Safari installs apps from the Share menu.
        </p>

        <ol className="install-steps">
          <li>
            <span className="install-step-n">1</span>
            <div>
              Tap <strong>Share</strong>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
                   style={{ verticalAlign: -3, margin: '0 3px' }}>
                <path d="M12 16V3M8 7l4-4 4 4M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
              </svg>
              at the bottom of Safari.
            </div>
          </li>
          <li>
            <span className="install-step-n">2</span>
            <div>Scroll down and tap <strong>Add to Home Screen</strong>.</div>
          </li>
          <li>
            <span className="install-step-n">3</span>
            <div>Tap <strong>Add</strong>. Eduvora appears with your other apps.</div>
          </li>
        </ol>

        <p className="install-note">
          Safari only — Chrome on iPhone can’t install apps. That’s Apple’s rule, not ours.
        </p>

        <button className="btn btn-accent" style={{ width: '100%', justifyContent: 'center' }}
                onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}
